import { lazy, type ComponentType } from "react";

type ModuleImport<T> = () => Promise<{ default: T }>;

const RELOAD_KEY = "mediflow_care_chunk_reload";
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1500;

function isChunkLoadError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    const msg = error.message.toLowerCase();
    return (
        msg.includes("failed to fetch dynamically imported module") ||
        msg.includes("loading chunk") ||
        msg.includes("loading css chunk") ||
        msg.includes("chunkloaderror") ||
        msg.includes("undefined is not an object") ||
        (msg.includes("failed to fetch") && error.name === "TypeError")
    );
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function importWithRetry<T>(
    importFn: ModuleImport<T>,
    retriesLeft: number = MAX_RETRIES,
): Promise<{ default: T }> {
    try {
        return await importFn();
    } catch (error) {
        if (!isChunkLoadError(error) || retriesLeft <= 0) {
            throw error;
        }
        console.warn(
            `[MediFlow Pflege] Chunk-Load fehlgeschlagen, Retry in ${RETRY_DELAY_MS}ms\u2026 (${retriesLeft} verbleibend)`,
        );
        await delay(RETRY_DELAY_MS);
        return importWithRetry(importFn, retriesLeft - 1);
    }
}

function attemptRecoveryReload(): boolean {
    if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
        return false;
    }
    const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY);
    if (alreadyReloaded) {
        console.error(
            "[MediFlow Pflege] Chunk-Load nach Reload immer noch fehlgeschlagen.",
        );
        return false;
    }
    console.warn("[MediFlow Pflege] Chunk-Load fehlgeschlagen \u2014 einmaliger Page-Reload\u2026");
    sessionStorage.setItem(RELOAD_KEY, Date.now().toString());
    window.location.reload();
    return true;
}

export function clearChunkReloadFlag(): void {
    if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem(RELOAD_KEY);
    }
}

export function lazyWithRetry<T extends ComponentType<unknown>>(importFn: ModuleImport<T>) {
    return lazy(async () => {
        try {
            const module = await importWithRetry(importFn);
            clearChunkReloadFlag();
            return module;
        } catch (error) {
            if (isChunkLoadError(error)) {
                const willReload = attemptRecoveryReload();
                if (willReload) {
                    return { default: (() => null) as unknown as T };
                }
            }
            throw error;
        }
    });
}

export function lazyWithRetryRoute<T extends ComponentType<unknown>>(importFn: ModuleImport<T>) {
    return async () => {
        try {
            const module = await importWithRetry(importFn);
            clearChunkReloadFlag();
            return { Component: module.default };
        } catch (error) {
            if (isChunkLoadError(error)) {
                const willReload = attemptRecoveryReload();
                if (willReload) {
                    return new Promise<{ Component: T }>(() => { });
                }
            }
            throw error;
        }
    };
}
